import { useEffect, useRef, useState, type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BlockLink } from 'BlockLink';
import '../locales/streamDashboard/streamDashboard-en.json';

/*
    Clips content until the student asks for the rest. The control shows only when scrollHeight exceeds
    clientHeight.
    The fade sits outside the clipped box: programmatic scroll inside it would move the fade off the cut.
*/

const FADE_HEIGHT = 'h-24';

type Props = {
    collapsed: boolean;
    onShowMore: () => void;
    children: ReactNode;

    /*
        The fade has to end in the colour behind the content, so the caller names it. The two band
        sections sit on the white band; the sidebar sits on the page background.
    */
    fadeClassName?: string;

    // The height the content is cut to. Anything taller than this is what the control reveals.
    collapsedMaxHeightClassName?: string;

    /*
        The space around the control. The 20px below is the legacy container's margin-bottom, and every
        caller keeps it. The 14px above suits a cut that ends flush with the content; a caller whose last
        visible element already carries a bottom margin passes just the 20px.
    */
    controlClassName?: string;
};

export const ShowMoreCollapse: FC<Props> = ({
    collapsed,
    onShowMore,
    children,
    fadeClassName = 'bg-gradient-to-b from-transparent to-white',
    collapsedMaxHeightClassName = 'max-h-[60vh]',
    controlClassName = 'mb-5 mt-3.5',
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'streamDashboard.streamDashboard' });
    const contentRef = useRef<HTMLDivElement>(null);
    const [hasHiddenContent, setHasHiddenContent] = useState(false);

    // Observe the element, not the window: content can grow without a resize, for example a late image.
    useEffect(() => {
        const content = contentRef.current;
        if (!content) return undefined;

        const measure = () => setHasHiddenContent(content.scrollHeight > content.clientHeight);

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(content);
        return () => observer.disconnect();
    }, [collapsed]);

    return (
        <>
            <div className="relative">
                <div
                    ref={contentRef}
                    data-testid="collapse-content"
                    className={collapsed ? `${collapsedMaxHeightClassName} overflow-hidden` : undefined}
                >
                    {children}
                </div>
                {collapsed && hasHiddenContent && (
                    <div
                        className={`pointer-events-none absolute inset-x-0 bottom-0 ${FADE_HEIGHT} ${fadeClassName}`}
                    />
                )}
            </div>

            {collapsed && hasHiddenContent && (
                <div className={`flex justify-center ${controlClassName}`}>
                    <BlockLink
                        // FIXME: the setTimeout works around https://github.com/heroui-inc/heroui/issues/1934,
                        // the same way CourseList does.
                        onPress={() => setTimeout(onShowMore)}
                        anchorIcon="chevron-down"
                        color="slate-grey"
                    >
                        {t('show_more')}
                    </BlockLink>
                </div>
            )}
        </>
    );
};
